import { Component, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy } from '@ngneat/until-destroy'
import { Actions } from '@ngrx/effects'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import { combineLatest, Observable } from 'rxjs'
import { take, map, filter } from 'rxjs/operators'
import { authSelectors } from 'src/app/core/auth/auth.selectors'
import { AppState } from 'src/app/core/core.state'
import { ConfigurationService } from 'src/app/shared/services/configuration.service'
import { Member } from '../../member/member'
import { Competition } from '../competition'
import { competitionPlayerSelectors } from '../competition-player/competition-player.selectors'
import { competitionStaffSelectors } from '../competition-staff/competition-staff.selectors'
import { CompetitionTeam } from '../competition-team/competition-team'
import { competitionTeamActions } from '../competition-team/competition-team.actions'
import { competitionTeamSelectors } from '../competition-team/competition-team.selectors'
import { competitionSelectors } from '../competition.selectors'
import { environment } from 'src/environments/environment'
import { competitionActions } from '../competition.actions'
import moment from 'moment-mini'
import { BreadCrumbActions } from 'src/app/shared/components/breadcrumb/breadcrumb.actions'
import { AppRoutesNS } from 'src/app/app-routes.namespace'

@UntilDestroy()
@Component({
  selector: 'competition-index',
  templateUrl: './index.component.html',
  styleUrls: ['./index.component.scss']
})
export class CompetitionIndexComponent implements OnInit {
  public isLoading$: Observable<boolean>
  public teams$: Observable<CompetitionTeam[]>
  public competitions$: Observable<Competition[]>
  public selectedTeam$: Observable<CompetitionTeam>
  public member$: Observable<Member>
  public onClickCreateButton: boolean = false
  public competitionEntities$: Observable<Dictionary<Competition>>
  public showOnlyCompetitionStanding =
    environment.moduleConfigs.competitionModule.showOnlyCompetitionStanding

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    public configurationService: ConfigurationService,
    private router: Router
  ) {}

  ngOnInit() {
    this.isLoading$ = this.store.select(
      competitionTeamSelectors.selectIsLoading
    )

    this.store.dispatch(
      BreadCrumbActions.addPageToHistory({
        pageName: 'competitionModule.competitions',
        url: AppRoutesNS.Competitions.COMPETITIONS,
        showBackArrow: false,
        titleName: 'competitionModule.competitions'
      })
    )
    this.member$ = this.store.select(authSelectors.selectMember)

    this.member$.pipe(take(1)).subscribe((member) => {
      this.store.dispatch(
        competitionTeamActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: {
              $or: [
                {
                  'competitionPlayers.memberId': member.id
                },
                {
                  'competitionStaff.memberId': member.id
                },
                {
                  teamCaptainPersonId: member.id
                }
              ]
            },
            join: [
              { field: 'competitionPlayers' },
              { field: 'competitionStaff' },
              { field: 'competitions' }
            ]
          })
        })
      )
    })

    this.store.dispatch(
      competitionActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            endDate: {
              [CondOperator.GREATER_THAN]: moment().startOf('day').toISOString()
            }
          },
          join: [
            { field: 'competitionPlayers' },
            { field: 'competitionStaff' },
            { field: 'competitions' }
          ]
        })
      })
    )

    this.teams$ = this.store.select(competitionTeamSelectors.selectAll)
    this.competitions$ = this.store.select(competitionSelectors.selectAll).pipe(
      map((competitions) => {
        return competitions
          .filter((competition) =>
            moment(competition.endDate).isAfter(moment().startOf('day'))
          )
          .sort((a, b) => a.description.localeCompare(b.description))
      })
    )

    this.teams$ = combineLatest([
      this.store.select(competitionTeamSelectors.selectAll),
      this.member$,
      this.store.select(competitionStaffSelectors.selectEntities),
      this.store.select(competitionPlayerSelectors.selectEntities)
    ]).pipe(
      map(
        ([
          teams,
          member,
          competitionStaffEntities,
          competitionPlayerEntities
        ]) => {
          let filteredTeams = []

          teams.forEach((team) => {
            if (!this.showOnlyCompetitionStanding) {
              team?.competitionPlayers?.forEach((playerId) => {
                if (
                  competitionPlayerEntities[playerId]?.memberId == member?.id &&
                  !filteredTeams.includes(team)
                ) {
                  filteredTeams.push(team)
                }
              })

              team?.competitionStaff?.forEach((staffId) => {
                if (
                  competitionStaffEntities[staffId]?.memberId == member?.id &&
                  !filteredTeams.includes(team)
                ) {
                  filteredTeams.push(team)
                }
              })
            }

            if (
              team?.teamCaptainPersonId == member?.id &&
              !filteredTeams.includes(team)
            ) {
              filteredTeams.push(team)
            }
          })

          return filteredTeams.sort((a, b) => b.id - a.id)
        }
      )
    )

    this.competitionEntities$ = this.store.select(
      competitionSelectors.selectEntities
    )
  }

  public onSelectTeam(element) {
    this.router.navigate(['/competition-team', element.target.value])
  }

  public onSelectCompetition(event: Event) {
    const target = event.target as HTMLSelectElement
    this.router.navigate(['/competitions', target.value])
  }

  public onClickCreateTeam() {
    this.router.navigateByUrl(`/competition-team/create`)
  }
}
